/**
 * CSV Import Wizard
 * Multi-step wizard for importing leads from CSV
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSVImportWizard = ({ user, onNavigate }) => {
  const [step, setStep] = useState(1);
  const [csvFile, setCsvFile] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const authHeaders = () => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  });

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      setCsvContent(content);
      
      try {
        const response = await fetch(`${API}/phase9/leads/import/parse`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ data: { csv_content: content } })
        });

        if (response.ok) {
          const data = await response.json();
          setHeaders(data.headers || []);
          setRowCount(data.row_count || 0);
          setColumnMapping(data.suggested_mapping || {});
          setStep(2);
          toast.success(`CSV parsed: ${data.row_count} rows found`);
        } else {
          toast.error('Failed to parse CSV');
        }
      } catch (error) {
        toast.error('Error parsing CSV');
        console.error(error);
      }
    };
    reader.readAsText(file);
  };

  const handleValidate = async () => {
    try {
      const response = await fetch(`${API}/phase9/leads/import/validate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: { csv_content: csvContent, column_mapping: columnMapping } })
      });

      if (response.ok) {
        const data = await response.json();
        setValidationResult(data);
        setStep(3);
        toast.success('Validation complete');
      } else {
        toast.error('Validation failed');
      }
    } catch (error) {
      toast.error('Error validating import');
    }
  };

  const handleExecuteImport = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API}/phase9/leads/import/execute`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          data: {
            csv_content: csvContent,
            column_mapping: columnMapping,
            skip_duplicates: true,
            tags: ['csv_import']
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Import complete: ${data.imported_count} leads imported`);
        setStep(4);
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      toast.error('Error executing import');
    } finally {
      setImporting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setCsvFile(null);
    setCsvContent('');
    setHeaders([]);
    setRowCount(0);
    setColumnMapping({});
    setValidationResult(null);
  };

  return (
    <div className="page-stack" data-testid="csv-import-wizard">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9B — CSV Import</p>
          <h2>CSV Lead Import Wizard</h2>
          <div className="hero-stats">
            <span>Step {step} of 4</span>
            {rowCount > 0 && <span>{rowCount} Rows</span>}
            {validationResult && <span>{validationResult.valid_count} Valid</span>}
          </div>
        </div>
      </section>

      {/* Progress Stepper */}
      <section className="glass-panel">
        <div className="flex items-center justify-between">
          {['Upload', 'Map Columns', 'Validate', 'Import'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={`flex items-center gap-2 ${step > idx + 1 ? 'text-[var(--gold)]' : step === idx + 1 ? 'text-white' : 'text-white/40'}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                  step > idx + 1 ? 'border-[var(--gold)] bg-[var(--gold)]/20' :
                  step === idx + 1 ? 'border-white bg-white/10' :
                  'border-white/20 bg-white/5'
                }`}>
                  {step > idx + 1 ? <CheckCircle2 size={16} /> : idx + 1}
                </div>
                <span className="text-sm font-medium">{label}</span>
              </div>
              {idx < 3 && <ArrowRight className="mx-4 text-white/20" size={16} />}
            </div>
          ))}
        </div>
      </section>

      {/* Step 1: Upload */}
      {step === 1 && (
        <section className="glass-panel">
          <div className="border-2 border-dashed border-white/20 rounded-2xl p-16 text-center hover:border-[var(--gold)]/30 transition-colors">
            <Upload className="h-16 w-16 text-white/40 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white/90 mb-2">Upload CSV File</h3>
            <p className="text-white/60 mb-6">Select a CSV file with lead data</p>
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="max-w-xs mx-auto bg-white/5 border-white/10 text-white file:text-white file:bg-white/10"
              data-testid="csv-file-input"
            />
          </div>
        </section>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && (
        <section className="glass-panel">
          <h3 className="text-lg font-semibold text-white/90 mb-4">Map CSV Columns to Lead Fields</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {headers.map((header) => (
              <div key={header} className="space-y-2">
                <Label className="text-white/70">{header}</Label>
                <Select
                  value={columnMapping[header] || ''}
                  onValueChange={(value) => setColumnMapping({ ...columnMapping, [header]: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select field" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clinic_name">Clinic Name</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="city">City</SelectItem>
                    <SelectItem value="state">State</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="category">Category</SelectItem>
                    <SelectItem value="rating">Rating</SelectItem>
                    <SelectItem value="review_count">Review Count</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-6">
            <Button className="ghost-btn" onClick={() => setStep(1)}>
              <ArrowLeft size={16} />
              Back
            </Button>
            <Button className="primary-btn" onClick={handleValidate} data-testid="validate-btn">
              Validate
              <ArrowRight size={16} />
            </Button>
          </div>
        </section>
      )}

      {/* Step 3: Validate */}
      {step === 3 && validationResult && (
        <section className="glass-panel space-y-6">
          <h3 className="text-lg font-semibold text-white/90">Validation Results</h3>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="glass-panel">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <p className="text-xs text-white/60">Valid Rows</p>
              </div>
              <strong className="text-2xl text-green-400">{validationResult.valid_count}</strong>
            </div>
            <div className="glass-panel">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="text-xs text-white/60">Invalid Rows</p>
              </div>
              <strong className="text-2xl text-red-400">{validationResult.invalid_count}</strong>
            </div>
            <div className="glass-panel">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-orange-400" />
                <p className="text-xs text-white/60">Duplicates</p>
              </div>
              <strong className="text-2xl text-orange-400">{validationResult.duplicate_count || 0}</strong>
            </div>
          </div>

          {validationResult.invalid_rows && validationResult.invalid_rows.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-white/90 mb-3">Invalid Rows (First 5)</h4>
              <div className="space-y-2">
                {validationResult.invalid_rows.slice(0, 5).map((row, idx) => (
                  <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-sm text-red-300 mb-1">Row {row.row_number}</p>
                    <ul className="text-xs text-red-400 list-disc list-inside">
                      {row.errors.map((error, eidx) => (
                        <li key={eidx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button className="ghost-btn" onClick={() => setStep(2)}>
              <ArrowLeft size={16} />
              Back
            </Button>
            <Button
              className="primary-btn"
              onClick={handleExecuteImport}
              disabled={importing || validationResult.valid_count === 0}
              data-testid="execute-import-btn"
            >
              {importing ? 'Importing...' : `Import ${validationResult.valid_count} Leads`}
              <ArrowRight size={16} />
            </Button>
          </div>
        </section>
      )}

      {/* Step 4: Complete */}
      {step === 4 && (
        <section className="glass-panel text-center py-16">
          <CheckCircle2 className="h-20 w-20 text-[var(--gold)] mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-white/90 mb-4">Import Complete!</h3>
          <p className="text-white/70 mb-8">Your leads have been successfully imported</p>
          <div className="flex gap-4 justify-center">
            <Button className="primary-btn" onClick={() => onNavigate('discovery')} data-testid="view-leads-btn">
              <FileText size={16} />
              View Leads
            </Button>
            <Button className="ghost-btn" onClick={resetWizard}>
              Import More
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default CSVImportWizard;
